"use client";

import Grid from "@/components/molecules/ProductGrid";
import { ArticleStoryblok } from "@/types/blok-types";
import clsx from "clsx";
import { Suspense, lazy, useMemo } from "react";
import { StoryblokStory as SBStory } from "storyblok-generate-ts";
import ClientArticleFilter from "./ClientArticleFilter";
import styles from "./article-listing-page.module.css";
import { SortOptions, useArticleFilters } from "./useArticleFilter";

/**
 * Dynamic import to avoid unknown Next + SB circular dependency reference error.
 * Running with SSR:true. So page is still rendered on server.
 */
const CardDynamic = lazy(() =>
  import("../ArticleCard/ArticleCard").then((mod) => ({ default: mod.Card }))
);

const UnsuspendedClientArticleGrid = ({
  articles,
  preview,
}: {
  articles?: SBStory<ArticleStoryblok>[];
  preview?: boolean;
}) => {
  const { sort } = useArticleFilters();

  const sortedArticles = useMemo(() => {
    if (sort === SortOptions.Newest) {
      return articles?.toSorted((a, b) => {
        return (
          new Date(a.sort_by_date || new Date()).getTime() -
          new Date(b.sort_by_date || new Date()).getTime()
        );
      });
    }
    return articles?.toSorted((a, b) => {
      return (
        new Date(b.sort_by_date || new Date()).getTime() -
        new Date(a.sort_by_date || new Date()).getTime()
      );
    });
  }, [articles, sort]);

  return (
    <section className={clsx(styles.products, "alp__grid")} data-layout="page">
      <ClientArticleFilter count={articles?.length} />

      <Grid>
        {sortedArticles?.map((article) => {
          return (
            <Suspense key={article.uuid}>
              <CardDynamic article={article} preview={preview} />
            </Suspense>
          );
        })}
      </Grid>
    </section>
  );
};

const ClientArticleGrid: typeof UnsuspendedClientArticleGrid = (props) => {
  return (
    <Suspense>
      <UnsuspendedClientArticleGrid {...props} />
    </Suspense>
  );
};

export default ClientArticleGrid;
